// ABOUTME: Hook wiring TabView3's pinned-views mode to userLayoutSlice and the tablesettings
// ABOUTME: backend. Resolves the pinned set for a screen and persists pin toggles.

import { useCallback } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useTranslation } from "react-i18next";

import { setPinnedViews } from "../../../newapi/global/userLayout/userLayoutSlice";
import { usePutTableSettingsMutation } from "../../../newapi/global/tableSettings/tableSettingsSlice";
import { showToastMessageUpdateRequest } from "../../../utils/toasts";
import { resolvePinnedKeys } from "./pinnedViews";

/**
 * usePinnedViews
 *
 * @param tabKey    identifier of the screen in userLayout.document.tabConfigs
 *                  (e.g. "warehouseOutboundOrders")
 * @param viewKeys  every view key of the screen, in canonical config order
 * @returns         { pinnedKeys, togglePin } — pinnedKeys resolved with defaults,
 *                  togglePin persists to redux and the backend.
 */
const usePinnedViews = (tabKey, viewKeys) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const store = useStore();
    const [putTableSettings] = usePutTableSettingsMutation();

    const savedKeys = useSelector(
        (state) => state.userLayout?.document?.tabConfigs?.[tabKey]?.pinnedKeys ?? null
    );

    const pinnedKeys = resolvePinnedKeys(savedKeys, viewKeys);

    // Same pattern as ShDataGrid3.syncToBackend: read fresh state via store.getState()
    // so the PUT includes the dispatch from the same call stack.
    const syncToBackend = useCallback(async () => {
        const doc = store.getState().userLayout?.document;
        try {
            const response = await putTableSettings({
                columnConfigs: doc?.columnConfigs ?? {},
                generalSettings: doc?.generalSettings ?? {},
                tabConfigs: doc?.tabConfigs ?? {},
            }).unwrap();
            showToastMessageUpdateRequest(t, response);
        } catch (err) {
            showToastMessageUpdateRequest(t, { success: false, error: err?.data?.error ?? "unknown" });
        }
    }, [putTableSettings, store, t]);

    const togglePin = useCallback(async (viewKey) => {
        // Resolve against fresh store state, not the render-scope pinnedKeys: the menu
        // stays open after a toggle, so a second click can fire before React re-renders
        // and would otherwise overwrite the first toggle with a stale snapshot.
        const savedNow = store.getState().userLayout?.document?.tabConfigs?.[tabKey]?.pinnedKeys ?? null;
        const currentKeys = resolvePinnedKeys(savedNow, viewKeys);

        const isPinned = currentKeys.includes(viewKey);
        const nextKeys = isPinned
            ? currentKeys.filter((key) => key !== viewKey)
            : [...currentKeys, viewKey];

        dispatch(setPinnedViews({ tabKey, pinnedKeys: nextKeys }));
        await syncToBackend();
    }, [dispatch, store, syncToBackend, tabKey, viewKeys]);

    return { pinnedKeys, togglePin };
};

export default usePinnedViews;
